import React, { useState } from "react";
import { MapPin, Mail, Clock } from "lucide-react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { offices, pageBanner } from "../mock";
import { translations } from "../translations";
import { useLanguage } from "../context/LanguageContext";
import { useToast } from "../hooks/use-toast";

const Contact = () => {
  const { lang } = useLanguage();
  const t = translations[lang];
  const c = t.contact;
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", tel: "", email: "", message: "" });

  const submit = (e) => {
    e.preventDefault();
    toast({ title: c.toastTitle, description: c.toastDesc });
    setForm({ name: "", tel: "", email: "", message: "" });
  };

  const inputCls = "w-full border-0 border-b border-slate-300 px-1 py-3 text-sm bg-transparent focus:outline-none focus:border-sky-accent transition-colors";

  return (
    <div>
      <PageHero title={c.pageTitle} image={pageBanner} />

      <section className="py-20 bg-white">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-16">
          {/* Left: offices + info */}
          <div>
            {offices.map((o, i) => {
              const info = c.offices[i];
              return (
                <Reveal key={i} delay={i * 100} className={i > 0 ? "mt-12" : ""}>
                  <h2 className="font-serif text-2xl font-bold brand-navy dash">{info.name}</h2>
                  <div className="flex gap-3 mt-6">
                    <MapPin size={18} className="text-sky-accent shrink-0 mt-0.5" />
                    <div className="text-slate-600 text-sm leading-relaxed">
                      {info.address.map((line, k) => (<div key={k}>{line}</div>))}
                    </div>
                  </div>
                  <div className="mt-5 rounded-sm overflow-hidden shadow-md border border-slate-200">
                    <iframe title={info.name} src={`https://www.google.com/maps?q=${encodeURIComponent(o.mapQuery)}&output=embed`} width="100%" height="220" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                  </div>
                </Reveal>
              );
            })}

            <Reveal delay={200}>
              <div className="grid sm:grid-cols-2 gap-6 mt-12">
                <div className="bg-[#f6f8f6] rounded-sm p-6">
                  <div className="flex items-center gap-2 text-sky-accent"><Mail size={18} /><span className="font-serif font-bold brand-navy">{c.enquiriesTitle}</span></div>
                  <a href={`mailto:${c.email}`} className="text-slate-600 text-sm mt-3 inline-block hover:text-sky-accent transition-colors">{c.email}</a>
                </div>
                <div className="bg-[#f6f8f6] rounded-sm p-6">
                  <div className="flex items-center gap-2 text-sky-accent"><Clock size={18} /><span className="font-serif font-bold brand-navy">{c.hoursTitle}</span></div>
                  <div className="text-slate-600 text-sm mt-3">
                    {c.hours.map((h, k) => (<div key={k}>{h}</div>))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right: form */}
          <Reveal delay={150}>
            <div className="bg-[#eef1f3] p-8 lg:p-10 rounded-sm">
              <h2 className="font-serif text-2xl font-bold brand-navy dash">{c.leaveMessage}</h2>
              <form onSubmit={submit} className="mt-8 space-y-6">
                <div>
                  <label className="text-xs uppercase tracking-wide text-slate-500">{c.labels.name}</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-slate-500">{c.labels.tel}</label>
                  <input value={form.tel} onChange={(e) => setForm({ ...form, tel: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-slate-500">{c.labels.email}</label>
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-slate-500">{c.labels.message}</label>
                  <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputCls} resize-none`} />
                </div>
                <button type="submit" className="pill-btn">{t.ui.send}</button>
              </form>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
